import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { EnvelopeSimple, ChatCircle, Megaphone, Prohibit } from "@phosphor-icons/react";

export default function CommPrefs({ contactId }) {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!contactId) return;
    const run = async () => {
      try {
        const r = await api.get(`/contacts/${contactId}/comm-prefs`);
        setPrefs(r.data.comm_prefs);
      } catch { setPrefs(null); }
    };
    run();
  }, [contactId]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put(`/contacts/${contactId}/comm-prefs`, patch);
      setPrefs(r.data.comm_prefs);
      toast.success("Preferences updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) return null;

  const Toggle = ({ label, Icon, value, onChange, testid, destructive = false }) => (
    <label className="flex items-center justify-between py-2" data-testid={testid}>
      <span className="flex items-center gap-2 text-sm">
        <Icon weight="bold" style={{ color: destructive ? "#FF3B30" : "var(--text-2)" }} />
        {label}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={!!value}
        onClick={() => onChange(!value)}
        disabled={saving}
        className="relative inline-flex h-5 w-10 items-center transition"
        style={{ background: value ? "var(--accent)" : "var(--border)" }}
      >
        <span
          className="inline-block h-4 w-4 bg-white transform transition"
          style={{ transform: value ? "translateX(22px)" : "translateX(2px)" }}
        />
      </button>
    </label>
  );

  return (
    <div className="border p-4" style={{ borderColor: "var(--border)" }} data-testid="comm-prefs">
      <div className="overline mb-3">// communication preferences</div>
      <Toggle
        label="Email allowed"
        Icon={EnvelopeSimple}
        value={prefs.email_allowed !== false}
        onChange={(v) => save({ email_allowed: v })}
        testid="pref-email"
      />
      <Toggle
        label="SMS allowed"
        Icon={ChatCircle}
        value={prefs.sms_allowed !== false}
        onChange={(v) => save({ sms_allowed: v })}
        testid="pref-sms"
      />
      <Toggle
        label="Marketing / campaigns allowed"
        Icon={Megaphone}
        value={prefs.marketing_allowed !== false}
        onChange={(v) => save({ marketing_allowed: v })}
        testid="pref-marketing"
      />
      <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
        <div className="overline mb-1">unsubscribe status</div>
        <select
          value={prefs.unsubscribe_status || "none"}
          onChange={(e) => save({ unsubscribe_status: e.target.value })}
          disabled={saving}
          className="w-full px-2 py-1 border bg-transparent text-sm"
          style={{ borderColor: "var(--border)" }}
          data-testid="pref-unsub-select"
        >
          <option value="none">none</option>
          <option value="email">email only</option>
          <option value="sms">sms only</option>
          <option value="global">global (all channels)</option>
        </select>
        {prefs.unsubscribe_status && prefs.unsubscribe_status !== "none" && (
          <div className="mt-2 text-xs flex items-center gap-1" style={{ color: "#FF3B30" }} data-testid="unsub-warning">
            <Prohibit weight="bold" /> Automation on blocked channels will be skipped.
          </div>
        )}
      </div>
    </div>
  );
}
